import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';

import '@/styles/main.css';
import '@/styles/store.css';
import { ToastContainer } from 'react-toastify';
import Header from '@/components/layouts/Header';
import { useHubSpot } from '@/hooks/useHubSpot';
import useBlotout, { BLOTOUT_EVENTS } from '@/hooks/useBlotout';
import { sessionStore } from '@/store/session/store';
import usePageView from '@/hooks/usePageView';
import { useResumeSessionValidation } from '@/hooks/useResumeSessionValidation';
import { useAccessDeniedToast } from '@/hooks/useAccessDeniedToast';
import Modal from '@/components/ui/modal/Modal';
import FunnelModalTitle from '@/components/ui/modal/funnel/FunnelModalTitle';
import Button from '@/components/ui/Button';
import ProgressBar from '@/features/funnel/progress/components/ProgressBar';
import { useSegmentPageTracking } from '@/hooks/useSegmentTracking';
import { usePersonalizedRecommendationFlow } from '@/hooks/usePersonalizedRecommendationFlow';
import clsx from 'clsx';
import { isGoogleOTP } from '@/utils/googleOTPData';
let lastTrackedPath: string | null = null;

const MainLayout: React.FC = () => {
  const { pathname } = useLocation();

  usePageView();
  useResumeSessionValidation();
  useAccessDeniedToast();
  useSegmentPageTracking();

  const { trackPageView } = useHubSpot();
  const { tag } = useBlotout();
  const { isSessionModalOpen, closeSessionModal } = sessionStore();
  const gbBlueskyPersonalizedRecommendationValue = usePersonalizedRecommendationFlow({
    pathname,
  });
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      const metaTag = document.querySelector('meta[name="viewport"]');
      const content = 'width=device-width, initial-scale=1.0, user-scalable=0';

      if (metaTag) {
        metaTag.setAttribute('content', content);
      }
    }
  }, []);

  useEffect(() => {
    if (lastTrackedPath === pathname) return;
    lastTrackedPath = pathname;

    trackPageView();
    tag({ event: BLOTOUT_EVENTS.PAGE_VIEW, once: false });
  }, [pathname, trackPageView, tag]);

  const handleSessionModalClose = () => {
    closeSessionModal();
  };

  const isPersonalizedIntroPage = useMemo(
    () =>
      (gbBlueskyPersonalizedRecommendationValue || isGoogleOTP()) && pathname.includes('/intro'),
    [gbBlueskyPersonalizedRecommendationValue, pathname],
  );

  return (
    <div
      className={clsx(
        'flex flex-col min-h-dvh h-full',
        isPersonalizedIntroPage &&
          'bg-gradient-to-b from-[var(--color-pine-green)] to-[var(--color-funnel-bg)]',
      )}
    >
      {!isPersonalizedIntroPage && (
        <>
          <Header />
          <ProgressBar />
        </>
      )}

      <main className="flex flex-col flex-1">
        <div className="contents h-full">
          <Outlet />
        </div>
      </main>

      <ToastContainer hideProgressBar closeButton={false} icon={false} />

      <Modal
        isOpen={isSessionModalOpen}
        onClose={handleSessionModalClose}
        title={<FunnelModalTitle title="Session Timeout" closeModal={handleSessionModalClose} />}
        overlayClose={handleSessionModalClose}
        buttons={
          <Button
            bgcolor="bg-funnel-bg hover:bg-funnel-bg"
            textcolor="text-ashgray"
            onClick={handleSessionModalClose}
            rounded="rounded-[46px]"
            className="border border-off-white !text-ashgray hover:!borer-[var(--color-gray)] !text-body-md !h-[43px] !normal-case shadow-off-white-shad"
            size="small"
          >
            Sign Back In
          </Button>
        }
      >
        <span className="text-silver text-body-md">
          You have been signed out due to inactivity.
        </span>
      </Modal>
    </div>
  );
};

export default MainLayout;
